import { isProduction } from "./utils";

const LOGGING_FLAG_KEY = "enablePlutoLogs"; // Use the same key for consistency across all pluto apps and libraries

// Capture original methods BEFORE modifying console
const originalConsole = {
  log: console.log.bind(console),
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

let isLocalStorageAvailableCache: boolean | null = null;

const isLocalStorageAvailable = (): boolean => {
  if (isLocalStorageAvailableCache !== null) {
    return isLocalStorageAvailableCache;
  }

  try {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      isLocalStorageAvailableCache = false;
      return false;
    }

    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    isLocalStorageAvailableCache = true;
    return true;
  } catch (e) {
    isLocalStorageAvailableCache = false;
    return false;
  }
};

const getLocalStorageItem = (key: string): string | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setLocalStorageItem = (key: string, value: string): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

// Detect if we're in development mode using Vite's env variable
const isDevelopmentEnvironment = (): boolean => {
  try {
    return !isProduction();
  } catch (e) {
    return false;
  }
};

export function setupConditionalLogging() {
  if (typeof window === "undefined") {
    originalConsole.info(
      "Skipping conditional logging setup in non-browser environment"
    );
    return;
  }

  const localStorageAvailable = isLocalStorageAvailable();

  // Each app needs to set its own flag since localStorage is per-domain
  if (isDevelopmentEnvironment() && localStorageAvailable) {
    if (getLocalStorageItem(LOGGING_FLAG_KEY) === null) {
      setLocalStorageItem(LOGGING_FLAG_KEY, "true");
      originalConsole.info(
        "%cPluto logs enabled automatically in development mode%c",
        "color: green; font-weight: bold;",
        "color: inherit;"
      );
    }
  }

  const logsEnabled = getLocalStorageItem(LOGGING_FLAG_KEY) === "true";

  if (logsEnabled) {
    console.log = originalConsole.log;
    console.debug = originalConsole.debug;
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;
  } else {
    // When logs are disabled, only silence non-error logs
    console.log = function () {};
    console.debug = function () {};
    console.info = function () {};
    console.warn = function () {};
    // Keep error logging always enabled
    console.error = originalConsole.error;
  }

  const color = logsEnabled ? "green" : "red";

  if (localStorageAvailable) {
    originalConsole.info(
      `%cPluto Frame logs ${logsEnabled ? "enabled" : "disabled"}%c ${
        !logsEnabled ? "(errors will still be shown)" : ""
      }`,
      `color: ${color}; font-weight: bold;`,
      "color: inherit;"
    );
  }
}
